/**
 * REGLAS DE LOS APLICADORES CON CARNÉ (F3 del plan del cuaderno, 26-09-2026).
 *
 * COPIA DE CLIENTE de `supabase/functions/_shared/applicatorRules.ts`: la
 * pantalla calcula lo mismo que el servidor (la caducidad, el aviso de 60 días
 * y quién saldrá como aplicador del parte). `applicatorRules.parity.test.ts`
 * compara las dos caso a caso: si se toca una, se toca la otra.
 *
 * Qué decide:
 *  - la CADUCIDAD del carné: obligatoria; si solo se sabe la expedición, son
 *    10 años (la BD hace lo mismo en su trigger);
 *  - cómo está HOY (vigente, caduca en menos de 60 días, caducado);
 *  - QUIÉN aplicó un parte cuando nadie lo elige (`pickApplicator`, D7);
 *  - la COPIA que se guarda en el parte (`applicatorSnapshot`), sin NIF (E11).
 *
 * Las fechas son 'AAAA-MM-DD' y se suman como texto: nada de zonas horarias.
 */

export const LICENSE_TYPES = ["basico", "cualificado", "fumigador", "piloto"] as const;
export type LicenseType = (typeof LICENSE_TYPES)[number];

export const APPLICATOR_KINDS = ["member", "service_company"] as const;
export type ApplicatorKind = (typeof APPLICATOR_KINDS)[number];

/** El carné de aplicador dura 10 años (RD 1311/2012). */
export const LICENSE_VALID_YEARS = 10;
/** Días que se avisa antes de que caduque. */
export const LICENSE_WARN_DAYS = 60;

export function isLicenseType(v: unknown): v is LicenseType {
  return typeof v === "string" && (LICENSE_TYPES as readonly string[]).includes(v);
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/** 'AAAA-MM-DD' + N años, sin pasar por Date (el 29-F cae en el 28). */
export function addYearsIso(isoDate: string | null | undefined, years: number): string | null {
  const m = ISO_DATE.exec(isoDate ?? "");
  if (!m) return null;
  const y = Number(m[1]) + years;
  const mo = m[2];
  let d = m[3];
  if (mo === "02" && d === "29") {
    const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
    if (!leap) d = "28";
  }
  return `${String(y).padStart(4, "0")}-${mo}-${d}`;
}

/** La caducidad escrita o, si no, la expedición + 10 años. Sin ninguna: null. */
export function licenseExpiresOn(issuedOn: string | null | undefined, expiresOn: string | null | undefined): string | null {
  if (expiresOn && ISO_DATE.test(expiresOn)) return expiresOn.slice(0, 10);
  return addYearsIso(issuedOn, LICENSE_VALID_YEARS);
}

/** Días entre dos fechas 'AAAA-MM-DD' (b − a). */
export function daysBetweenIso(a: string, b: string): number | null {
  const ma = ISO_DATE.exec(a);
  const mb = ISO_DATE.exec(b);
  if (!ma || !mb) return null;
  const ta = Date.UTC(Number(ma[1]), Number(ma[2]) - 1, Number(ma[3]));
  const tb = Date.UTC(Number(mb[1]), Number(mb[2]) - 1, Number(mb[3]));
  return Math.round((tb - ta) / 86_400_000);
}

export type LicenseStatus = "ok" | "soon" | "expired" | "unknown";

/** Cómo está el carné en una fecha: el día de HOY en el perfil, el de la aplicación en el parte. */
export function licenseStatus(expiresOn: string | null | undefined, onDate: string): LicenseStatus {
  if (!expiresOn) return "unknown";
  const left = daysBetweenIso(onDate, expiresOn);
  if (left == null) return "unknown";
  if (left < 0) return "expired";
  if (left <= LICENSE_WARN_DAYS) return "soon";
  return "ok";
}

/** Lo que hace falta de un aplicador para elegirlo y copiarlo en el parte. */
export interface ApplicatorRow {
  id: string;
  user_id: string | null;
  kind: string;
  display_name: string;
  nif?: string | null;
  ropo_number?: string | null;
  license_type?: string | null;
  license_issued_on?: string | null;
  license_expires_on?: string | null;
  is_advisor?: boolean | null;
  advisor_ropo?: string | null;
  active: boolean;
  archived_at?: string | null;
}

const usable = (a: ApplicatorRow) => a.active && !a.archived_at;

export interface PickedApplicator<T extends ApplicatorRow> {
  /** El carné (fila de company_applicators), si lo hay. */
  applicator: T | null;
  /** La PERSONA que aplicó (null si fue una empresa de servicios o no se sabe). */
  userId: string | null;
}

/**
 * QUIÉN APLICÓ, en este orden (D7):
 *   1. el elegido en la tarea («⋯» → «Aplicador…»), si sigue activo: una
 *      persona con carné o una EMPRESA DE SERVICIOS;
 *   2. el ASIGNADO a la tarea (aunque la complete otro: en Nijasol, 189 de 311
 *      partes los completó otra persona), con su carné si lo tiene;
 *   3. quien completa o registra el parte, con su carné si lo tiene.
 * `created_by` no se toca: sigue siendo quién lo registró.
 * Una persona sin carné dado de alta sigue siendo el aplicador (sin carné: el
 * cuaderno lo enseña como hueco); no se salta a otra persona que sí lo tenga.
 */
export function pickApplicator<T extends ApplicatorRow>(
  list: readonly T[],
  opts: { chosenId?: string | null; assignedUserId?: string | null; completedByUserId?: string | null },
): PickedApplicator<T> {
  const live = list.filter(usable);
  if (opts.chosenId) {
    const chosen = live.find((a) => a.id === opts.chosenId);
    if (chosen) return { applicator: chosen, userId: chosen.kind === "member" ? chosen.user_id : null };
  }
  const person = opts.assignedUserId || opts.completedByUserId || null;
  if (!person) return { applicator: null, userId: null };
  const own = live.find((a) => a.kind === "member" && a.user_id === person) ?? null;
  return { applicator: own, userId: person };
}

/**
 * La copia que se guarda en `spray_logs.applicator_snapshot`.
 *
 * SIN NIF, a propósito (E11): `spray_logs` lo lee cualquier rol de la empresa
 * (trabajador y perito incluidos) y el NIF es un dato personal que solo ven el
 * admin y el encargado (RLS de `company_applicators`). El cuaderno (1.2) lo
 * saca de `company_applicators` por `id`, no de esta copia.
 */
export interface ApplicatorSnapshot {
  id: string | null;
  tipo: ApplicatorKind;
  user_id: string | null;
  nombre: string | null;
  ropo: string | null;
  carne: LicenseType | null;
  expedido: string | null;
  caduca: string | null;
  /** Cómo estaba el carné EL DÍA de la aplicación. */
  estado: LicenseStatus | "sin_carne";
  asesor: boolean;
  ropo_asesor: string | null;
}

export function applicatorSnapshot(
  picked: { applicator: ApplicatorRow | null; userId: string | null },
  opts: { personName?: string | null; date: string },
): ApplicatorSnapshot | null {
  const a = picked.applicator;
  if (!a && !picked.userId) return null;
  const clean = (s: string | null | undefined) => {
    const v = (s ?? "").trim();
    return v ? v : null;
  };
  if (!a) {
    return {
      id: null,
      tipo: "member",
      user_id: picked.userId,
      nombre: clean(opts.personName),
      ropo: null,
      carne: null,
      expedido: null,
      caduca: null,
      estado: "sin_carne",
      asesor: false,
      ropo_asesor: null,
    };
  }
  const caduca = licenseExpiresOn(a.license_issued_on, a.license_expires_on);
  return {
    id: a.id,
    tipo: a.kind === "service_company" ? "service_company" : "member",
    user_id: a.kind === "member" ? a.user_id : null,
    nombre: clean(a.display_name) ?? clean(opts.personName),
    ropo: clean(a.ropo_number),
    carne: isLicenseType(a.license_type) ? a.license_type : null,
    expedido: a.license_issued_on ?? null,
    caduca,
    estado: licenseStatus(caduca, opts.date),
    asesor: !!a.is_advisor && !!clean(a.advisor_ropo),
    ropo_asesor: a.is_advisor ? clean(a.advisor_ropo) : null,
  };
}
